import { createReducer } from '@reduxjs/toolkit';
import difference from 'lodash/difference';

import { getSourceDatasetIds } from 'Internals/pipes';
import { DownstreamMap } from 'Types/common.types';
import type { Pipe, PipeResponse } from 'Types/pipes.types';
import { disconnected } from '../thunks/subscriptions.thunks';
import {
  pipeAdded,
  pipeLoaded,
  pipeUpdated,
  pipesLoadAllFinished,
  pipesRemoved,
} from '../thunks/pipes.thunks';

type DownstreamsState = DownstreamMap;
export const initialState: DownstreamsState = {};

function _setDownstream(datasetIds: string[], pipe: Pipe | PipeResponse, state: DownstreamsState) {
  // Beware, modifies in place
  for (const id of datasetIds) {
    if (state[id]) {
      state[id][pipe._id] = true;
    } else {
      state[id] = { [pipe._id]: true };
    }
  }
}

function _clearDownstream(
  datasetIds: string[],
  pipe: Pipe | PipeResponse,
  state: DownstreamsState
) {
  for (const id of datasetIds) {
    if (state[id] && state[id][pipe._id]) {
      // We are in the immer produce function so it's okay
      // but beware if used outside of this context, mutates state
      delete state[id][pipe._id];
    }
  }
}

function setDownstream(pipe: Pipe | PipeResponse, state: DownstreamsState) {
  // Beware, modifies in place
  const sourceDatasetIds = getSourceDatasetIds(pipe);
  if (sourceDatasetIds.length < 1) {
    return;
  } else {
    _setDownstream(sourceDatasetIds, pipe, state);
  }
}

/*
Note: We are using the builder callback for creating a typesafe reducer.
See https://redux-toolkit.js.org/usage/usage-with-typescript#building-type-safe-reducer-argument-objects
*/
export const downstreamsReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(disconnected, () => initialState)
    .addCase(pipeAdded, (state, action) => {
      setDownstream(action.payload, state);
    })
    .addCase(pipeUpdated, (state, { payload }) => {
      const newSourceDatasetIds = getSourceDatasetIds(payload.pipe);
      const oldSourceDatasetIds = getSourceDatasetIds(payload.oldPipe);
      const toAdd = difference(newSourceDatasetIds, oldSourceDatasetIds);
      const toDelete = difference(oldSourceDatasetIds, newSourceDatasetIds);
      if (toAdd.length > 0) {
        _setDownstream(toAdd, payload.pipe, state);
      }
      if (toDelete.length > 0) {
        _clearDownstream(toDelete, payload.oldPipe, state);
      }
    })
    .addCase(pipeLoaded, (state, action) => {
      setDownstream(action.payload.pipe, state);
    })
    .addCase(pipesLoadAllFinished, (state, action) => {
      const newState = {};
      for (let i = 0; i < action.payload.length; i++) {
        setDownstream(action.payload[i], newState);
      }
      return newState;
    })
    .addCase(pipesRemoved, (state, { payload }) => {
      payload.forEach((p) => {
        for (const id of p.sourceDatasetIds) {
          if (state[id]) {
            delete state[id][p.id];
          }
        }
      });
    })
);
